import type { GameResponse } from '@warcarp/api-types'
import { computeSeatMap } from './seatMap'

export interface StartGameWarning {
  title: string
  message: string
}

/**
 * Explain why starting now may leave invited players or seats behind.
 * A full lobby with no pending invitations returns null so the normal Start
 * action remains one tap.
 */
export function getStartGameWarning(
  game: Pick<GameResponse, 'players' | 'invitations' | 'maxPlayers'>,
): StartGameWarning | null {
  const pendingInvites = game.invitations.filter((invitation) => invitation.status === 'pending').length
  const openSlots = computeSeatMap(game.players, game.invitations, game.maxPlayers).filter(
    (seat) => seat.kind === 'open',
  ).length

  if (pendingInvites === 0 && openSlots === 0) return null

  const reasons: string[] = []
  if (pendingInvites > 0) {
    reasons.push(
      `${pendingInvites} invited ${pendingInvites === 1 ? 'player has' : 'players have'} not joined`,
    )
  }
  if (openSlots > 0) {
    reasons.push(`${openSlots} open ${openSlots === 1 ? 'slot' : 'slots'} remain`)
  }

  const playerCount = game.players.length
  return {
    title: `Start with ${playerCount} ${playerCount === 1 ? 'player' : 'players'}?`,
    message: `${reasons.join('. ')}. Are you sure you want to start with ${playerCount} ${playerCount === 1 ? 'player' : 'players'}?`,
  }
}
